import { dragHandleGap, topPositionAdjustment } from '../consts';

export type SurfacePlacement = {
	left: number;
	top: number;
	transform: string;
};

export type SurfaceSide = 'left' | 'right';

export type SurfacePlacementOptions = {
	layout: string;
	nodeRect: Pick<DOMRect, 'left' | 'right' | 'top' | 'width'>;
	nodeType: string;
	nodeTypeWithLevel: string;
	parentNodeType?: string;
	side: SurfaceSide;
};

export const getAbsoluteSurfacePlacement = ({
	offsetParentRect,
	offsetParentScrollLeft,
	offsetParentScrollTop,
	placement,
}: {
	offsetParentRect?: Pick<DOMRect, 'left' | 'top'>;
	offsetParentScrollLeft: number;
	offsetParentScrollTop: number;
	placement: SurfacePlacement;
}): SurfacePlacement => ({
	...placement,
	left: placement.left - (offsetParentRect?.left ?? 0) + offsetParentScrollLeft,
	top: placement.top - (offsetParentRect?.top ?? 0) + offsetParentScrollTop,
});

/**
 * Positions the surface beside the legacy drag-handle slot, on either the left or right edge
 * of the target node. Node-type-specific gap/top-adjustment maths (layouts, tables, media, cards,
 * extensions, etc.) live in one place so left/right edge cases can't drift out of sync.
 *
 * The left side grows away from the node without knowing its own width, so it needs
 * `translateX(-100%)` to anchor its right edge at `nodeRect.left` and extend backward. The right
 * side grows in the normal box-flow direction (away from the node, into the margin), so the gap
 * can be folded straight into `left` with no transform needed.
 */
export const getSurfacePlacement = ({
	layout,
	nodeRect,
	nodeType,
	nodeTypeWithLevel,
	parentNodeType,
	side,
}: SurfacePlacementOptions): SurfacePlacement => {
	if (nodeType === 'layoutColumn') {
		// nodeRect.left + width/2 === nodeRect.right - width/2 — side-independent.
		return {
			left: nodeRect.left + nodeRect.width / 2,
			top: nodeRect.top,
			transform: 'translate(-50%, -50%)',
		};
	}

	const gap = dragHandleGap(nodeType, parentNodeType);
	const top = nodeRect.top + topPositionAdjustment(nodeTypeWithLevel, layout);

	return side === 'right'
		? { left: nodeRect.right + gap, top, transform: '' }
		: { left: nodeRect.left, top, transform: `translateX(calc(-100% - ${gap}px))` };
};
